/**
 * Import din exportul CSV al 1Password (coloane Title, Url, Username,
 * Password, OTPAuth, Notes, ...). Numele coloanelor variază între versiuni și
 * platforme, așa că le potrivim tolerant: fără majuscule, spații sau semne.
 * Coloanele necunoscute (Favorite, Archived, Tags, ...) sunt ignorate.
 */
import { parseCsv } from '@/features/vault/transfer/csv';
import { mapLogin, type LoginEntry } from '@/features/vault/transfer/mapLogin';
import { TransferError, type ParsedImport } from '@/features/vault/transfer/types';

const COLUMN_ALIASES: Record<keyof LoginEntry, readonly string[]> = {
  title: ['title', 'name'],
  url: ['url', 'urls', 'website', 'websites', 'loginuri', 'loginurl'],
  username: ['username', 'user', 'login', 'loginusername', 'email'],
  password: ['password', 'loginpassword'],
  notes: ['notes', 'notesplain', 'note'],
  totp: ['otpauth', 'otp', 'totp', 'onetimepassword', 'logintotp'],
};

function normalizeHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function parseOnePasswordCsv(raw: string): ParsedImport {
  const rows = parseCsv(raw);
  const result: ParsedImport = { items: [], skipped: [] };
  if (rows.length === 0) return result;

  const headers = rows[0].map(normalizeHeader);
  const column = {} as Record<keyof LoginEntry, number>;
  for (const key of Object.keys(COLUMN_ALIASES) as Array<keyof LoginEntry>) {
    // Ordinea aliasurilor dă prioritatea, nu ordinea coloanelor din fișier.
    column[key] = -1;
    for (const alias of COLUMN_ALIASES[key]) {
      const index = headers.indexOf(alias);
      if (index >= 0) {
        column[key] = index;
        break;
      }
    }
  }
  // Fără titlu și fără parolă nu e un export de parole — probabil alt fișier.
  if (column.title < 0 && column.password < 0) {
    throw new TransferError('csv_no_known_columns');
  }

  for (const row of rows.slice(1)) {
    const cell = (key: keyof LoginEntry): string =>
      column[key] >= 0 ? (row[column[key]] ?? '') : '';
    const mapped = mapLogin({
      title: cell('title').trim(),
      url: cell('url').trim(),
      username: cell('username').trim(),
      // Parola rămâne exact cum e în fișier: spațiile pot face parte din ea.
      password: cell('password'),
      notes: cell('notes').trim(),
      totp: cell('totp').trim(),
    });
    result.items.push(...mapped.items);
    result.skipped.push(...mapped.skipped);
  }
  return result;
}
